import { useState } from 'react';
import { 
  Truck, AlertTriangle, Users, MapPin, Circle, Square, 
  Minus, ArrowRight, ChevronDown, ChevronRight, GripVertical,
  Flame, Zap, Skull, Wind, Building2, Cross, Shield, Heart,
  ParkingSquare, Utensils, UserCog, User, CircleDashed, MoveRight
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { cn } from '../lib/utils';
import { ArrowTypeIcon, HoseToolIcon } from './ToolIcons';
import { ARROW_TYPE_BY_SYMBOL, HOSE_SYMBOL_ID } from '../lib/lineStyles';
import {
  vehicleZeichenSpec, symbolZeichenSpec, renderZeichen,
  isArrowSymbol, arrowSvg, sichtungSvg
} from '../lib/taktischeZeichen';

const ICON_MAP = {
  'flame': Flame,
  'zap': Zap,
  'skull': Skull,
  'wind': Wind,
  'building-2': Building2,
  'cross': Cross,
  'shield': Shield,
  'heart-pulse': Heart,
  'parking-square': ParkingSquare,
  'utensils': Utensils,
  'user-cog': UserCog,
  'user': User,
  'circle': Circle,
  'circle-dashed': CircleDashed,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowRight,
  'move-right': MoveRight,
  'triangle-alert': AlertTriangle,
  'users': Users,
  'radiation': AlertTriangle,
};

// Ziehen per Maus (Desktop) oder Antippen und dann auf die Karte tippen (Touch)
const itemClassName = (active) => cn(
  "flex items-center gap-2 p-2 touch:min-h-11 rounded-md border cursor-grab active:cursor-grabbing transition-all group select-none",
  active
    ? "bg-lunefire-red/15 border-lunefire-red/40"
    : "bg-secondary/30 hover:bg-lunefire-red/10 hover:border-lunefire-red/30 border-transparent"
);

const VehicleItem = ({ vehicle, onDragStart, onSelect, active }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, 'vehicle', vehicle)}
      onClick={() => onSelect?.('vehicle', vehicle)}
      role="button"
      aria-pressed={active}
      className={itemClassName(active)}
      data-testid={`vehicle-${vehicle.id}`}
    >
      <GripVertical className="hidden md:block w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      <VehiclePreview vehicle={vehicle} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate font-body">{vehicle.name}</p>
      </div>
    </div>
  );
};

const VehiclePreview = ({ vehicle }) => {
  const zeichen = renderZeichen(vehicleZeichenSpec(vehicle));
  if (zeichen) {
    return <img src={zeichen.url} alt="" className="h-6 w-10 object-contain" draggable={false} />;
  }
  return (
    <div 
      className="w-8 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
      style={{ backgroundColor: vehicle.color }}
    >
      {vehicle.type}
    </div>
  );
};

// Vorschau des Kartenzeichens; arrowSvg/sichtungSvg liefern statisches Markup ohne Nutzereingaben
const SymbolPreview = ({ symbol }) => {
  // Pfeile und Schlauchleitung werden als Linie gezeichnet – Vorschau wie auf der Karte
  if (ARROW_TYPE_BY_SYMBOL[symbol.id]) {
    return <ArrowTypeIcon type={ARROW_TYPE_BY_SYMBOL[symbol.id]} className="w-8 h-4" />;
  }
  if (symbol.id === HOSE_SYMBOL_ID) {
    return <HoseToolIcon className="w-8 h-6" />;
  }
  const IconComponent = ICON_MAP[symbol.icon] || AlertTriangle;
  const markup = isArrowSymbol(symbol) ? arrowSvg(symbol, 0, 24) : sichtungSvg(symbol, 20);
  if (markup) {
    return <div className="w-8 h-6 flex items-center justify-center" dangerouslySetInnerHTML={{ __html: markup }} />;
  }
  const zeichen = renderZeichen(symbolZeichenSpec(symbol));
  if (zeichen) {
    return <img src={zeichen.url} alt="" className="h-6 w-8 object-contain" draggable={false} />;
  }
  return (
    <div 
      className="w-8 h-6 rounded flex items-center justify-center"
      style={{ color: symbol.color }}
    >
      <IconComponent className="w-4 h-4" />
    </div>
  );
};

const SymbolItem = ({ symbol, onDragStart, onSelect, active }) => {
  
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, 'symbol', symbol)}
      onClick={() => onSelect?.('symbol', symbol)}
      role="button"
      aria-pressed={active}
      className={itemClassName(active)}
      data-testid={`symbol-${symbol.id}`}
    >
      <GripVertical className="hidden md:block w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      <SymbolPreview symbol={symbol} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate font-body">{symbol.name}</p>
      </div>
    </div>
  );
};

const CategorySection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button 
          variant="ghost" 
          className="w-full justify-between px-2 py-2 h-auto hover:bg-lunefire-red/10"
          data-testid={`category-${title.toLowerCase().replace(/\s/g, '-')}`}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-lunefire-red" />
            <span className="font-display text-sm uppercase tracking-wider font-semibold">{title}</span>
          </div>
          {isOpen ? <ChevronDown className="w-4 h-4 text-lunefire-red" /> : <ChevronRight className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1 px-1 pb-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const categoryIcons = {
  gefahr: AlertTriangle,
  schaden: Flame,
  fuehrung: Building2,
  raum: ParkingSquare,
  medizin: Cross,
  sichtung: Circle,
  atemschutz: Wind,
  zone: CircleDashed,
  wasser: Zap,
  pfeil: ArrowRight,
  infra: MapPin,
  komm: Wind,
  sonstige: MapPin
};

const categoryNames = {
  gefahr: 'Gefahren',
  schaden: 'Schadensereignisse',
  fuehrung: 'Führungsstellen',
  raum: 'Räume/Plätze',
  medizin: 'Medizinisch',
  sichtung: 'Sichtung',
  atemschutz: 'Atemschutz',
  zone: 'Zonen/Absperrung',
  wasser: 'Wasserversorgung',
  pfeil: 'Richtungen',
  infra: 'Infrastruktur',
  komm: 'Kommunikation',
  sonstige: 'Sonstige'
};

// Fahrzeuge und Symbole nach Kategorien – in der Seitenleiste und im Handy-Panel genutzt
export const ElementCatalog = ({ vehicles = [], symbols = [], onDragStart, onSelect, activeId }) => {
  // Group symbols by category
  const symbolsByCategory = symbols.reduce((acc, symbol) => {
    const cat = symbol.category || 'sonstige';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(symbol);
    return acc;
  }, {});

  // Group vehicles by type
  const vehiclesByType = vehicles.reduce((acc, vehicle) => {
    const type = vehicle.type || 'Sonstige';
    if (!acc[type]) acc[type] = [];
    acc[type].push(vehicle);
    return acc;
  }, {});

  return (
    <div className="p-2 space-y-1">
      {/* Vehicles Section */}
      <CategorySection title="Fahrzeuge" icon={Truck} defaultOpen={true}>
        {Object.entries(vehiclesByType).map(([type, typeVehicles]) => (
          <div key={type} className="space-y-1">
            <p className="text-xs text-muted-foreground font-medium px-2 pt-2">{type}</p>
            {typeVehicles.map(vehicle => (
              <VehicleItem 
                key={vehicle.id} 
                vehicle={vehicle} 
                onDragStart={onDragStart}
                onSelect={onSelect}
                active={activeId === vehicle.id}
              />
            ))}
          </div>
        ))}
      </CategorySection>

      {/* Symbols Sections */}
      {Object.entries(symbolsByCategory).map(([category, categorySymbols]) => (
        <CategorySection 
          key={category}
          title={categoryNames[category] || category}
          icon={categoryIcons[category] || MapPin}
          defaultOpen={category === 'gefahr'}
        >
          {categorySymbols.map(symbol => (
            <SymbolItem 
              key={symbol.id} 
              symbol={symbol}
              onDragStart={onDragStart}
              onSelect={onSelect}
              active={activeId === symbol.id}
            />
          ))}
        </CategorySection>
      ))}
    </div>
  );
};

export const Sidebar = ({ vehicles = [], symbols = [], onDragStart, onSelect, activeId, isCollapsed, onToggle }) => {
  if (isCollapsed) {
    return (
      <div className="w-12 border-r border-border bg-card/95 backdrop-blur flex flex-col items-center py-4 gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onToggle}
          className="hover:bg-lunefire-red/10"
          data-testid="expand-sidebar-btn"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Truck className="w-5 h-5 text-lunefire-red" />
        <AlertTriangle className="w-5 h-5 text-lunefire-orange" />
      </div>
    );
  }

  return (
    <div className="w-64 border-r border-border bg-card/95 backdrop-blur flex flex-col" data-testid="sidebar">
      <div className="p-3 border-b border-lunefire-red/20 flex items-center justify-between bg-gradient-to-r from-lunefire-red/5 to-transparent">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-lunefire-charcoal dark:text-white">Elemente</h2>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onToggle}
          className="h-8 w-8 hover:bg-lunefire-red/10"
          data-testid="collapse-sidebar-btn"
        >
          <ChevronDown className="w-4 h-4 rotate-90" />
        </Button>
      </div>
      
      <ScrollArea className="flex-1">
        <ElementCatalog
          vehicles={vehicles}
          symbols={symbols}
          onDragStart={onDragStart}
          onSelect={onSelect}
          activeId={activeId}
        />
      </ScrollArea>
      <p className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground text-center">
        created by freakms
      </p>
    </div>
  );
};

export default Sidebar;
